import React, { useRef, useState } from 'react';
import Bluebird from 'bluebird';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { Input } from '@/components/DesignLibrary/Atoms';
import DocumentUploader, { DocumentUploaderRef } from '@/components/Document/DocumentUploader';
import { nanoid } from 'nanoid';
import Upload from '@components/Icons/Upload';
import Button from '@/components/DesignLibrary/Button';

import s from './UploadBankStatementModal.module.scss';
import SizeErrorModal from './SizeErrorModal';

export type ModalConfig = {
  step: 'choose' | 'manual';
  fieldValues: {
    institutionName: string;
    accountName?: string;
  };
  disabledFields: Array<'INSTITUTION_NAME' | 'ACCOUNT_NAME'>;
};

type UploadedDocument = Awaited<ReturnType<typeof tsClient.documents.getDocumentByUploadId>>;

type Props = {
  bankAccountIntegrationId: string;
  creatorId: string;
  modalConfig?: ModalConfig;
  onError: (error: Error | null) => void;
  onFinish: () => void;
};

const ManualUpload: React.FC<Props> = ({
  bankAccountIntegrationId,
  creatorId,
  modalConfig,
  onError,
  onFinish,
}) => {
  const [institutionName, setInstitutionName] = useState(modalConfig?.fieldValues?.institutionName ?? '');
  const [accountName, setAccountName] = useState(modalConfig?.fieldValues?.accountName ?? '');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSizeErrorModalOpen, setIsSizeErrorModalOpen] = useState<boolean>(false);
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);

  const { getData: addBankAccountDocumentsFn } = useNoodleApi(tsClient.bankAccountIntegrations.addBankAccountDocuments, { toastOnError: true });

  const handleFinishUpload = async (uploadedDocs: UploadedDocument[]): Promise<void> => {
    if (uploadedDocs && uploadedDocs.length) {
      await Bluebird.mapSeries(uploadedDocs, async uploadedDoc => {
        await addBankAccountDocumentsFn({
          accountName,
          accountSubType: accountName,
          bankAccountIntegrationId,
          documentId: uploadedDoc?.id,
          documentName: uploadedDoc?.fileName,
          institutionName,
          type: 'manual',
        });
      });
    }
    onFinish();
    setIsUploading(false);
  };

  return (
    <>
      <div className={s.container}>
        <Input
          value={institutionName}
          disabled={modalConfig?.disabledFields?.includes('INSTITUTION_NAME')}
          onChange={setInstitutionName}
          label="Institution Name"
          compact
        />
        <Input
          value={accountName}
          disabled={modalConfig?.disabledFields?.includes('ACCOUNT_NAME')}
          onChange={setAccountName}
          label="Account Name"
          compact
        />

        <div className={s.fileSelectionSection}>
          <Upload size={24} weight="fill" />
          <p className="caption">Upload your bank statements<br/>in PDF or JPEG format</p>
          <Button
            variant="secondary"
            size="sm"
            loading={isUploading}
            disabled={!institutionName || !accountName}
            onClick={() => documentUploaderRef.current?.uploadDocument()}>
            Select files
          </Button>
        </div>
        <DocumentUploader
          id={nanoid()}
          ref={documentUploaderRef}
          creatorId={creatorId}
          onUploadProgressChange={() => null}
          onUploadError={onError}
          onFinishUpload={handleFinishUpload}
          onUploadStart={() => setIsUploading(true)}
          isMultiple={true}
          checkSize={true}
          isSizeError={setIsSizeErrorModalOpen}
        />
      </div>
      {isSizeErrorModalOpen && <SizeErrorModal setInvalidDocModalOpen={setIsSizeErrorModalOpen} />}
    </>
  );
};

export default ManualUpload;